import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import { api } from '@/lib/axios';

interface User {
  _id: string;
  id: string;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  role?: 'user' | 'broker' | 'admin';
  brokerProfile?: {
    companyName?: string;
    rut?: string;
    licenseNumber?: string;
    phone?: string;
    address?: string;
    subscriptionPlan: 'free' | 'professional' | 'enterprise';
    subscriptionExpiry?: string;
    country?: string;
  };
  listedProperties?: string[];
  rentedProperties?: string[];
  purchasedProperties?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  register: (data: RegisterData) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      
      setUser: (user) => set({ user }),
      setToken: (token) => {
        if (token) {
          localStorage.setItem('token', token);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
        }
        set({ token });
      },

      register: async (data: RegisterData) => {
        try {
          console.log('[auth] Intentando registrar usuario:', { ...data, password: '***' });
          
          const response = await api.post('/users/register', data);
          console.log('[auth] Respuesta de registro:', response.data);
          
          if (!response.data || !response.data.token || !response.data.user) {
            throw new Error('Respuesta inválida del servidor');
          }
          
          const { token, user } = response.data;
          
          // Configurar token y usuario
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          localStorage.setItem('token', token);
          
          set({ user, token });
          
          toast.success('Usuario registrado correctamente');
        } catch (error: any) {
          console.error('[auth] Error en registro:', error);
          
          // Mostrar mensaje de error específico
          let errorMessage = 'Error al registrar usuario';
          
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          toast.error(errorMessage);
          throw error;
        }
      },

      loadUser: async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            set({ user: null, token: null });
            return;
          }

          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get('/users/me');
          
          if (!response.data) {
            throw new Error('No se pudo obtener la información del usuario');
          }
          
          set({ user: response.data, token });
        } catch (error: any) {
          console.error('[auth] Error al cargar usuario:', error);
          
          let errorMessage = 'Error al cargar usuario';
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          toast.error(errorMessage);
          
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          set({ user: null, token: null });
        }
      },

      login: async (email: string, password: string) => {
        try {
          console.log('[auth] Intentando iniciar sesión:', { email, password: '***' });
          
          const response = await api.post('/users/login', { email, password });
          console.log('[auth] Respuesta de login:', response.data);
          
          if (!response.data || !response.data.token || !response.data.user) {
            throw new Error('Respuesta inválida del servidor');
          }
          
          const { token, user } = response.data;
          
          // Configurar token y usuario
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          localStorage.setItem('token', token);
          
          set({ user, token });
          
          toast.success('Sesión iniciada correctamente');
        } catch (error: any) {
          console.error('[auth] Error en login:', error);
          
          // Mostrar mensaje de error específico
          let errorMessage = 'Credenciales inválidas';
          
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          toast.error(errorMessage);
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        set({ user: null, token: null });
        toast.success('Sesión cerrada correctamente');
      },
    }),
    {
      name: 'auth-storage',
      skipHydration: false,
    }
  )
); 